import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { AgTechApiErrorCodes } from "@agtech/core/data/http/AgTechApiErrorCodes";
import { useAgTechHttpDeleteRequest } from "@agtech/core/data/http/AgTechApiDeleteRequests";
import { useAgTechHttpPostEntityRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { Payment } from "./InvoicePaymentData";
import { useAgTechHttpPutEntityRequest } from "@agtech/core/data/http/AgTechApiPutRequests";
import { InvoicePaymentDataGetInvoicePaymentsQuery } from "./InvoicePaymentDataQueries";
import { invalidateInvoiceQueries, InvoiceDataGetPaidInvoicesQuery, InvoiceQueryKey } from "../InvoicingDataQueries";
import { useCurrentSale } from "../../sales/SaleDataStore";
import { useQueryClient } from "@tanstack/react-query";

// Shared by all three payment actions below - a concurrency conflict means someone else changed
// this invoice since the client last loaded it, so refresh the invoice details rather than just
// showing a generic error, mirroring how Lot actions handle AgTechApiErrorCodes.ConcurrencyError.
const showInvoiceConcurrencyConflict = (props: { showError: (header: string, details?: string) => void }) => {
    props.showError(
        'This invoice was already updated by someone else',
        'Refreshing the invoice with the latest details. Please review and try again.'
    );
};

export const usePaymentDataWritePaymentAction = (): AgTechDataAction<Payment, Payment> => {
    const currentSale = useCurrentSale();
    const queryClient = useQueryClient();
    const salePaymentSubmitRequest = useAgTechHttpPostEntityRequest<Payment>();

    return {
        name: 'WritePayment',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity ? 'Updating payment...' : 'Creating payment...',
            actionConfirmationMessage: `Successfully ${props.originalEntity ? 'updated' : 'created' } payment`
        }),
        validate: async props  => {
            if (props.submittedEntity.amount <= 0) {
                props.validation.failWithWarning("Please specify a valid payment amount");
            }

            if (props.submittedEntity.invoiceId == 0) {
                props.validation.failWithWarning('An invoice was not specified. This is a bug.');
            }

            if (props.submittedEntity.payerId == 0) {
                props.validation.failWithWarning('Please specify a payer.');
            }
        },
        action: async props => {
            return await salePaymentSubmitRequest.post({
                path: `Invoice/${props.submittedEntity.invoiceId}/Payment`,
                postedData: props.submittedEntity,
            });
        },
        onFailure: async props => {
            if (props.error.errorCode === AgTechApiErrorCodes.ConcurrencyError) {
                showInvoiceConcurrencyConflict(props);

                queryClient.invalidateQueries({ queryKey: [InvoiceQueryKey] });
            }
            else {
                props.showError(props.error.message ?? 'An error occurred. Please try again.');
            }
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, {
                saleId: currentSale.id,
                invoiceId: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoicePaymentDataGetInvoicePaymentsQuery,
                queryContext: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoiceDataGetPaidInvoicesQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    }
}

export const usePaymentDataEditPaymentAction = (): AgTechDataAction<Payment, Payment> => {
    const currentSale = useCurrentSale();
    const queryClient = useQueryClient();
    const salePaymentSubmitRequest = useAgTechHttpPutEntityRequest<Payment>();

    return {
        name: 'EditPayment',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity ? 'Updating payment...' : 'Creating payment...',
            actionConfirmationMessage: `Successfully ${props.originalEntity ? 'updated' : 'created' } payment`
        }),
        validate: async props  => {
            if (props.submittedEntity.amount <= 0) {
                props.validation.failWithWarning("Please specify a valid payment amount");
            }

            if (props.submittedEntity.invoiceId == 0) {
                props.validation.failWithWarning('An invoice was not specified. This is a bug.');
            }

            if (props.submittedEntity.payerId == 0) {
                props.validation.failWithWarning('Please specify a payer.');
            }
        },
        action: async props => {
            return await salePaymentSubmitRequest.put({
                path: `Invoice/${props.submittedEntity.invoiceId}/Payment`,
                postedData: props.submittedEntity,
            });
        },
        onFailure: async props => {
            if (props.error.errorCode === AgTechApiErrorCodes.ConcurrencyError) {
                showInvoiceConcurrencyConflict(props);

                queryClient.invalidateQueries({ queryKey: [InvoiceQueryKey] });
            }
            else {
                props.showError(props.error.message ?? 'An error occurred. Please try again.');
            }
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, {
                saleId: currentSale.id,
                invoiceId: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoicePaymentDataGetInvoicePaymentsQuery,
                queryContext: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoiceDataGetPaidInvoicesQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    }
}

export const usePaymentDataDeletePaymentAction = (): AgTechDataAction<Payment, boolean> => {
    const currentSale = useCurrentSale();
    const queryClient = useQueryClient();
    const paymentDeleteRequest = useAgTechHttpDeleteRequest()

    return {
        name: 'DeletePayment',
        getConfiguration: props => ({
            actionExecutionMessage: 'Deleting payment',
            actionConfirmationMessage: 'Successfully deleted payment'
        }),
        confirm: async props => {
            return await props.appContext.confirmations.askForConfirmation({
                content: {
                    header: `Are you sure you want to delete the payment with amount "$${props.submittedEntity.amount}" from the invoice?`,
                    details: "The payment will be deleted and will no longer be applied to the invoice.",
                    width: '500px',
                    confirmationButtonText: 'Delete Payment'
                },
                submission: {
                    actionExecutionMessage: 'Deleting payment...',
                    actionConfirmationMessage: 'Payment deleted'
                }
            })
        },
        action: async props => {
            const timestampQuery = props.submittedEntity.invoiceTimestamp
                ? `?invoiceTimestamp=${encodeURIComponent(props.submittedEntity.invoiceTimestamp)}`
                : '';

            return await paymentDeleteRequest.delete({
                path: `Invoice/${props.submittedEntity.invoiceId}/Payment/${props.submittedEntity.id}${timestampQuery}`
            });
        },
        onFailure: async props => {
            if (props.error.errorCode === AgTechApiErrorCodes.ConcurrencyError) {
                showInvoiceConcurrencyConflict(props);

                queryClient.invalidateQueries({ queryKey: [InvoiceQueryKey] });
            }
            else {
                props.showError(props.error.message ?? 'An error occurred. Please try again.');
            }
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, {
                saleId: currentSale.id,
                invoiceId: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoicePaymentDataGetInvoicePaymentsQuery,
                queryContext: props.submittedEntity.invoiceId
            });

            props.invalidateQuery({
                query: InvoiceDataGetPaidInvoicesQuery,
                queryContext: { saleId: currentSale.id }
            });
        }
    }
}